import { COUNT_UNREAD_NOTIFICATION_INTERVAL } from "constants/index";

import { ElementType, useState } from "react";

import {
  Avatar,
  Badge,
  Box,
  Collapse,
  Drawer,
  ListItemIcon,
  ListItemText,
  List as MUIList,
  ListItemButton as MUIListItemButton,
  Typography as MUITypography,
  Popover,
  Stack,
  SvgIcon,
  Tooltip,
} from "@mui/material";
import avatarSrc from "assets/images/avatar.png";
import { Paths } from "configs";
import { shadows } from "configs/shadows";
import { useNotificationControllerCountMyNotifications } from "generated";
import { Link, Link as RouterLink, useLocation } from "react-router-dom";

import { useSidebar } from "./Sidebar.hooks";
import {
  Container,
  Divider,
  Flag,
  List,
  ListItemButton,
  Logo,
  LogoWrapper,
  LogoutButton,
  MenuBarPlaceholder,
  MenuBarTrack,
  MenuContainer,
  MenuScrollbar,
  MenuWrapper,
  SubList,
  UserInfo,
  UserWrapper,
} from "./Sidebar.styles";
import { SidebarMenuItem, SidebarProps } from "./Sidebar.types";
import { ChevronRightIcon, LogOut01Icon } from "../Icon";
import Typography from "../Typography";

const checkSubmenuActive = (submenu: SidebarMenuItem[]): boolean => {
  let active = false;
  for (const s of submenu) {
    if (s.path && window.location.pathname.includes(s.path)) {
      active = true;
      break;
    }
    if (Array.isArray(s.children) && s.children.length > 0) {
      active = checkSubmenuActive(s.children);
      if (active) break;
    }
  }
  return active;
};

const MenuRendering = (
  data: SidebarProps["menu"],
  level: number,
  collapsed: boolean
) => {
  const location = useLocation();

  const [menuId, setMenuId] = useState<string | null>(null);

  const onClickHandler = (id: string) => {
    setMenuId((prev) => (prev === id ? null : id));
  };

  return data.map(({ icon, id, path, title, hideInMenu, children = [] }) => {
    const isChildrenEmpty =
      children.length === 0 ||
      children.filter((e) => !e.hideInMenu).length === 0;
    const isOpen = menuId === id;
    const isHyperlink = !!path;
    let isActive = false;
    if (isHyperlink) {
      isActive =
        path === Paths.App
          ? location.pathname === Paths.App
          : location.pathname.includes(path);
    } else {
      isActive = checkSubmenuActive(children);
    }
    return !hideInMenu ? (
      <Box key={id}>
        <ListItemButton
          to={isHyperlink ? path : undefined}
          component={isHyperlink ? RouterLink : undefined}
          onClick={() => (!isChildrenEmpty ? onClickHandler(id) : null)}
          level={level}
          open={isOpen}
          collapsed={collapsed}
          active={isActive}
        >
          <ListItemIcon>
            <SvgIcon
              fontSize="small"
              component={icon as unknown as ElementType}
            />
          </ListItemIcon>
          <ListItemText primary={title} />
          {!isChildrenEmpty ? <ChevronRightIcon /> : null}
        </ListItemButton>
        {!isChildrenEmpty ? (
          <Collapse in={!collapsed ? isOpen : false}>
            <SubList level={level} numberOfChildren={children?.length ?? 0}>
              {MenuRendering(children, level + 1, collapsed)}
            </SubList>
          </Collapse>
        ) : null}
      </Box>
    ) : null;
  });
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed = false,
  menu,
  bottomMenu,
  userInfo,
  languages,
  defaultLanguage,
  onHover,
  onBlur,
  onLogout,
  onLanguageChange,
  anchor,
}) => {
  const { language } = useSidebar({
    defaultLanguage,
    languages,
    onBlur,
    onLanguageChange,
  });

  const { data } = useNotificationControllerCountMyNotifications({
    query: {
      refetchInterval: (dataInterval, query) => {
        if (dataInterval === undefined && query.state.errorUpdateCount <= 3) {
          return false;
        }
        // eslint-disable-next-line
        return COUNT_UNREAD_NOTIFICATION_INTERVAL;
      },
    },
  });

  const currentLanguage = languages?.find((i) => i.id === language.value);

  return (
    <Container
      collapsed={collapsed}
      onMouseEnter={onHover}
      onMouseLeave={onBlur}
    >
      <Drawer
        open
        variant="persistent"
        anchor={anchor ?? "left"}
        PaperProps={{
          sx: { overflowX: "hidden" },
        }}
      >
        <Stack
          justifyContent="space-between"
          height="100%"
          gap={4}
          paddingY={4}
        >
          <LogoWrapper to={Paths.App}>
            <Logo collapsed={collapsed} />
          </LogoWrapper>
          <Divider />
          <MenuContainer className="menu-container">
            <MenuWrapper className="menu-wrapper">
              <List className="menu-list" component="nav">
                {MenuRendering(menu, 1, collapsed)}
              </List>
              <List component="nav">
                {currentLanguage ? (
                  <ListItemButton
                    level={1}
                    collapsed={collapsed}
                    onClick={language.open}
                  >
                    <Flag
                      src={currentLanguage.flagIcon}
                      alt={currentLanguage.name}
                    />
                    <ListItemText primary={currentLanguage.name} />
                  </ListItemButton>
                ) : null}
                {MenuRendering(bottomMenu, 1, collapsed)}
              </List>
            </MenuWrapper>
            <MenuBarTrack className="menu-bar-track">
              <MenuBarPlaceholder className="menu-bar-placeholder" />
              <MenuScrollbar className="menu-scrollbar" />
            </MenuBarTrack>
          </MenuContainer>
          {userInfo ? (
            <>
              <Divider />
              <UserWrapper>
                <Link to={Paths.notificationPath}>
                  <Badge
                    color="error"
                    overlap="circular"
                    variant={data?.data && data.data > 0 ? "dot" : undefined}
                  >
                    <Avatar src={avatarSrc} sx={{ flexBasis: "40px" }} />
                  </Badge>
                </Link>
                <UserInfo collapsed={collapsed}>
                  <Tooltip arrow title={userInfo.name}>
                    <MUITypography fontWeight={600}>
                      {userInfo.name}
                    </MUITypography>
                  </Tooltip>
                  <Tooltip arrow title={userInfo.email}>
                    <MUITypography>{userInfo.email}</MUITypography>
                  </Tooltip>
                </UserInfo>
                <LogoutButton collapsed={collapsed} onClick={onLogout}>
                  <LogOut01Icon fontSize="small" color="inherit" />
                </LogoutButton>
              </UserWrapper>
            </>
          ) : null}
        </Stack>
      </Drawer>
      {languages && languages?.length > 0 ? (
        <Popover
          open={!!language.ref}
          anchorEl={language.ref}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { boxShadow: shadows.xs } } }}
          onClose={language.close}
        >
          <MUIList>
            {languages.map((i) => (
              <MUIListItemButton
                key={i.id}
                onClick={() => language.change(i.id)}
              >
                <Stack width="100%" direction="row" alignItems="center" gap={2}>
                  <Flag src={i.flagIcon} alt={i.name} />
                  <Typography type="TextSM" fontWeight={500} flex={1}>
                    {i.name}
                  </Typography>
                </Stack>
              </MUIListItemButton>
            ))}
          </MUIList>
        </Popover>
      ) : null}
    </Container>
  );
};

export default Sidebar;
